/**
 * shareLandingStamp — the share→signup attribution hand-off (R4 WP-3).
 *
 * A visitor lands on a public surface (`/r/:uuid`, `/u/:handle/:slug`, or a
 * `/u/:handle` profile), then signs up. The server cannot see that first hop:
 * `Referer` is same-origin and stripped on several of these routes, and by the
 * time `/auth/signup` is POSTed the SPA has already navigated away. So the
 * browser stamps WHICH KIND of surface it arrived on, and `SignInGate` replays
 * that kind with the signup request.
 *
 * ── What is stored ───────────────────────────────────────────────────────────
 *
 * A KIND, and nothing else: `"share_recipe"` or `"share_profile"`. Never a URL,
 * never a recipe uuid / slug / handle, never a title. That bound is the whole
 * design — the value becomes a funnel `detail` token, and the funnel forbids
 * both per-recipe cardinality on a once-per-user event and any PII
 * (apps/api/src/analytics/funnel.ts invariant 2). The api re-validates against
 * its own enum, so a tampered value is dropped rather than recorded.
 *
 * ── Why sessionStorage ───────────────────────────────────────────────────────
 *
 * The signup happens in the same tab and the same sitting, so tab-scoped storage
 * is the right lifetime: it dies with the tab, never leaks between tabs, and
 * survives no longer than the visit it describes. (Contrast pendingGroupJoin /
 * pendingFriendInvite, which must survive a whole email round trip and therefore
 * use localStorage with an explicit TTL.)
 *
 * Every access is guarded — Safari private mode throws on `sessionStorage`
 * access — and every failure degrades to "no attribution", never to a broken
 * signup.
 */

export type ShareLandingKind = "share_recipe" | "share_profile";

const KEY = "proof.share-landing";

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function isKind(value: string | null): value is ShareLandingKind {
  return value === "share_recipe" || value === "share_profile";
}

/**
 * Record that this tab's visit started on a public share surface.
 *
 * First stamp wins: a visitor who lands on a recipe and then browses to the
 * owner's profile is attributed to the recipe that brought them, which is the
 * acquisition question being asked. Re-stamping on every mount would attribute
 * to whichever page they happened to sign up from instead.
 */
export function stampShareLanding(kind: ShareLandingKind): void {
  const s = storage();
  if (!s) return;
  try {
    if (isKind(s.getItem(KEY))) return;
    s.setItem(KEY, kind);
  } catch (err) {
    console.debug({ event: "share_landing_stamp_failed", err });
  }
}

/** Read this tab's share-landing kind, or null when the visit did not start on one. */
export function readShareLanding(): ShareLandingKind | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(KEY);
    return isKind(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Forget the stamp — called once the signup that consumed it has succeeded. */
export function clearShareLanding(): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(KEY);
  } catch (err) {
    console.debug({ event: "share_landing_clear_failed", err });
  }
}
